import {literalUnion} from '@raison/io-ts-tools';
import {expectEither, HttpClient, HttpClient$, POST} from '@raison/network-facade';
import {queryOptions} from '@tanstack/react-query';
import {type} from 'io-ts';
import {atom} from 'jotai';
import {atomWithMutation, atomWithQuery, queryClientAtom} from 'jotai-tanstack-query';
import {AddressStatus, ServiceProvider, updateUserInfo, UserInfoQueryOptions$} from '~/entities/user';
import {BrokerAccountService} from '~/processes/app-maintenance';

export const userInfo = {
    options$: UserInfoQueryOptions$,
};

export const userTariff = (() => {
    const query = atomWithQuery(get =>
        queryOptions({
            ...get(UserInfoQueryOptions$),
            select: data => data.tariff,
        })
    );

    return {
        query,
    };
})();

async function sendBrokerAccountRequest(http: HttpClient) {
    const response = await http('v1/securities/request/sign', POST()).then(
        ...expectEither({
            200: type({
                sign_on_securities: literalUnion('requested', 'rejected', 'signed', 'none'),
            }),
        })
    );

    if (response.status === 'Abort') {
        throw response.reason;
    }

    return {
        nextStatus: response.json.sign_on_securities,
    };
}

export const brokerAccount: BrokerAccountService = (() => {
    const query = atomWithQuery(get =>
        queryOptions({
            ...get(UserInfoQueryOptions$),
            select: data => {
                const RaisonSecuritiesLTD = data.serviceProviders.find(
                    sp => sp.serviceProvider === ServiceProvider.RaisonSecuritiesLTD
                );
                return RaisonSecuritiesLTD?.status;
            },
        })
    );

    const request = atomWithMutation<Awaited<ReturnType<typeof sendBrokerAccountRequest>>>(get => {
        return {
            mutationKey: ['broker-request'],
            mutationFn: () => sendBrokerAccountRequest(get(HttpClient$)),
            onSuccess({nextStatus}) {
                const options = get(UserInfoQueryOptions$);
                get(queryClientAtom).setQueryData(options.queryKey, current =>
                    updateUserInfo.securitiesLTDRequested(current, nextStatus)
                );
            },
            gcTime: Infinity,
        };
    });

    return {
        query,
        request,
    };
})();

export const kycUserInfo = (() => {
    const query = atomWithQuery(get =>
        queryOptions({
            ...get(UserInfoQueryOptions$),
            select: data => ({
                username: data.username,
                status: data.status,
                uuid: data.uuid,
            }),
        })
    );

    return {
        query,
    };
})();

export const addressDocsStatus = (() => {
    const query = atomWithQuery(get =>
        queryOptions({
            ...get(UserInfoQueryOptions$),
            select: data => {
                const status = AddressStatus.toLegacy(data.dossier?.address.verifiedStatus);
                switch (status) {
                    case '':
                        return 'not_verified';
                    case 'success':
                        return 'verified';
                    case 'pending':
                        return status;
                }
            },
        })
    );

    return {
        query,
    };
})();

export const userProfileIdentity = (() => {
    const query = atomWithQuery(get =>
        queryOptions({
            ...get(UserInfoQueryOptions$),
            select: data => ({
                email: data.email,
                phoneNumber: data.username,
                addressStatus: data.dossier?.address.verifiedStatus,
                onboardingStatus: data.status,
                givenName: data.dossier?.personal.givenName,
                familyName: data.dossier?.personal.familyName,
                address: data.dossier?.address,
            }),
        })
    );

    return {
        query,
    };
})();

export const phoneNumber = (() => {
    const query = atomWithQuery(get => queryOptions({...get(UserInfoQueryOptions$), select: data => data.username}));
    query.debugLabel = 'phoneNumber';
    return {query};
})();

export const email = (() => {
    const query = atomWithQuery(get => queryOptions({...get(UserInfoQueryOptions$), select: data => data.email}));
    query.debugLabel = 'email';
    return {query};
})();

export const isPaymentRestricted = (() => {
    const query = atomWithQuery(get =>
        queryOptions({
            ...get(UserInfoQueryOptions$),
            select: data => {
                const hasRaisonMarketUAB = data.serviceProviders.some(
                    sp => sp.serviceProvider === ServiceProvider.RaisonMarketUAB && sp.status === 'signed'
                );
                return hasRaisonMarketUAB === false;
            },
        })
    );
    return {query};
})();

export const alerts = (() => {
    const query = atomWithQuery(get =>
        queryOptions({
            ...get(UserInfoQueryOptions$),
            select: data => data.notifications,
        })
    );
    const list = atom(get => get(query).data ?? []);
    return {
        query,
        list,
    };
})();
